import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';

import { NETHONE_PROFILER_URL } from '#src/config.ts';
import { sendSafeError } from '#src/lib/error.ts';
import { randomUUID } from '#src/lib/uuid.ts';

import { NethoneContext } from './index.ts';

const NETHONE_PROFILER_ID = 'external-profiler';
const FALLBACK_DELAY_MS = 15000;

declare global {
  interface Window {
    dftp: {
      init: (options: any) => void;
      profileCompleted: () => Promise<void>;
      finishBehavGathering: () => void;
    };
  }
}

type Props = {
  children: ReactNode;
};

let resolve: (value: string | null | PromiseLike<string | null>) => void;
const attemptReference = new Promise<string | null>(r => {
  resolve = r;
});

// Nethone insert an Iframe containing a Canvas on each dftp.init without removing it.
// This is conflicting with 3D : Browsers support only a limited number of contexts, depending on device performance and available memory.
const cleanUpInsertedIframe = () => {
  const iframe = document.querySelector(
    'iframe[src="about:blank"][style="width: 99px; height: 99px; border: 0px; position: absolute; top: -999px; display: block;"]'
  );
  iframe?.remove();
};

const cleanup = (child: Node) => {
  if (window.dftp) {
    // https://panel.nethone.io/docs/integration_guide/profiler/web_services_cdn
    // In some cases, like a payment page that is part of a Single Page Application, you may want to stop the gathering
    // of behavioral data after user leaves the payment page. to do that call dftp.finishBehavGathering(). No
    // behavioral data will be gathered after calling this function.
    try {
      window.dftp.finishBehavGathering();
    } catch (e) {
      // ignore any kind of exceptions here
      sendSafeError(e);
    }
  }
  document.body.removeChild(child);
};

export const NethoneProvider = ({ children }: Props) => {
  const reference = useRef<string>(randomUUID());

  useEffect(() => {
    if (!NETHONE_PROFILER_URL) {
      resolve!(null);
      return () => {};
    }

    const node = document.getElementById(NETHONE_PROFILER_ID);
    if (node) {
      return () => cleanup(node);
    }

    // regenerate attempt reference on each mount
    reference.current = randomUUID();

    // load profiler script
    const script = document.createElement('script');
    script.src = NETHONE_PROFILER_URL;
    script.id = NETHONE_PROFILER_ID;
    script.async = true;
    script.addEventListener('load', () => {
      if (window.dftp) {
        try {
          window.dftp.init({
            attemptReference: reference.current,
            sensitiveFields: ['number', 'cvc'],
          });
        } catch (e) {
          // when the profiler API gets rate-limited, it throws an exception related to finishBehavGathering
          // yet we don't want to block the user because of that
          sendSafeError(e);
          resolve!(null);
          return;
        }
        window.dftp
          .profileCompleted()
          .then(() => {
            resolve!(reference.current);
          })
          .catch(e => {
            sendSafeError(e);
            resolve!(null);
          });
      }
    });
    document.body.appendChild(script);

    // fallback in case the profiler doesn't complete
    const fallback = setTimeout(() => resolve!(null), FALLBACK_DELAY_MS);
    return () => {
      clearTimeout(fallback);
      cleanup(script);
      cleanUpInsertedIframe();
    };
  }, []);

  return (
    <NethoneContext
      value={{
        attemptReference,
      }}
    >
      {children}
    </NethoneContext>
  );
};
